import { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { toast } from "sonner"
import { useAuth } from "@/contexts/AuthContext"

export default function Login() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")

  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()
  const redirectTo = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ""}`
    : "/"

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError("")

    try {
      const result = await login({ email, password })

      if (result.success) {
        toast.success("Login successful", {
          description: `Welcome back, ${result.user?.first_name || 'User'}!`
        })
        navigate(redirectTo, { replace: true })
      } else {
        setError(result.error || "Login failed")
      }
    } catch (err) {
      setError("An unexpected error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-[72px] flex items-center justify-center z-10 [background:linear-gradient(90deg,var(--nav-from)_0%,var(--nav-to)_100%)]">
        <img src="/logo.svg" alt="Logo" className="h-[40px] w-auto object-contain" />
      </div>

      {/* Left panel — form */}
      <div className="flex-1 flex items-center justify-center bg-page-bg px-8 py-12 md:py-12 pt-[104px]">
        <div className="w-full max-w-[380px] flex flex-col gap-8">

          {/* Heading */}
          <div className="flex flex-col gap-1.5 text-center md:text-left">
            <h1 className="text-[28px] font-medium leading-tight text-page-title">Sign in</h1>
            <p className="text-sm text-page-muted">Enter your credentials to continue</p>
          </div>

          {/* Form */}
          <div className="bg-card-bg rounded-[8px] px-7 py-7 shadow-[0_4px_24px_0_var(--shadow-blue)]">
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              {error && (
                <div className="px-4 py-3 rounded-[5px] text-sm bg-[var(--error-bg)] text-error-text">
                  {error}
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-medium text-page-text">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  disabled={isLoading}
                  className="h-9 px-3 rounded-[5px] border text-sm outline-none transition-colors disabled:opacity-50 bg-card-bg border-[var(--input-border)] text-page-text focus:border-nav-active"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="password" className="text-sm font-medium text-page-text">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={isLoading}
                  className="h-9 px-3 rounded-[5px] border text-sm outline-none transition-colors disabled:opacity-50 bg-card-bg border-[var(--input-border)] text-page-text focus:border-nav-active"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="h-9 rounded-[5px] text-sm font-medium text-white cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed mt-1 bg-nav-active hover:bg-nav-active-dark transition-colors"
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Right panel — branding */}
      <div className="hidden md:flex flex-col justify-between w-[45%] px-[60px] py-[50px] nav-sidebar-bg">
        {/* Logo */}
        <div className="w-[260px]">
          <img src="/logo.svg" alt="Logo" className="w-full h-auto object-contain" />
        </div>

        {/* Main text */}
        <div className="flex flex-col gap-5">
          <h2 className="text-[36px] font-medium leading-tight text-white">
            Business automation,<br />done right.
          </h2>
          <p className="text-sm leading-relaxed text-nav-text-dim">
            One platform for governed data, business workflows, AI agents, and the apps that run on them — from raw sources to deployed automation.
          </p>
        </div>

        {/* Bottom tagline */}
        <p className="text-xs text-nav-text-faint">
          TIVA Cortex — Business Automation Platform
        </p>
      </div>

      {/* Build version — bottom corner, unobtrusive */}
      <span className="fixed bottom-2 right-3 text-[11px] text-page-muted opacity-50 select-none pointer-events-none">
        v{__CORTEX_VERSION__}
      </span>

    </div>
  )
}
